"use client";

import { useState } from "react";
import { showToast } from "@/components/ui/sonner";
import { googleOauth, microsoftOauth } from "@/lib/api/generated";
import { currentCometToken } from "@/lib/cometly";
import { Icon } from "@/components/ui/icon";
import { useFingerprintEventId } from "../hooks/use-fingerprint-event-id";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.537 8.167c0-.52-.047-1.02-.133-1.5H8v2.84h3.947a3.374 3.374 0 0 1-1.467 2.213v1.847h2.38c1.387-1.28 2.18-3.16 2.18-5.4z"
        fill="currentColor"
      />
      <path
        d="M8 15.333c1.98 0 3.64-.653 4.853-1.773l-2.38-1.847c-.653.44-1.487.707-2.473.707-1.907 0-3.527-1.287-4.107-3.02H1.453v1.893C2.66 13.687 5.133 15.333 8 15.333z"
        fill="currentColor"
      />
      <path
        d="M3.893 9.393a4.812 4.812 0 0 1 0-2.793V4.707H1.453a8.003 8.003 0 0 0 0 6.58l2.44-1.893z"
        fill="currentColor"
      />
      <path
        d="M8 3.587c1.08 0 2.04.373 2.807 1.093l2.1-2.1C11.633 1.393 9.973.667 8 .667 5.133.667 2.66 2.313 1.453 4.707l2.44 1.893C4.473 4.88 6.093 3.587 8 3.587z"
        fill="currentColor"
      />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="7" height="7" fill="currentColor" />
      <rect x="8.5" y="0.5" width="7" height="7" fill="currentColor" />
      <rect x="0.5" y="8.5" width="7" height="7" fill="currentColor" />
      <rect x="8.5" y="8.5" width="7" height="7" fill="currentColor" />
    </svg>
  );
}

interface SocialAuthButtonsProps {
  variant?: "signin" | "signup";
}

export function SocialAuthButtons({
  variant = "signin",
}: SocialAuthButtonsProps) {
  const [loading, setLoading] = useState<"google" | "microsoft" | null>(null);
  const getFingerprintEventId = useFingerprintEventId();
  const label = variant === "signup" ? "Sign up" : "Sign in";

  async function handleOAuth(provider: "google" | "microsoft") {
    setLoading(provider);
    try {
      // The Cometly pixel can't auto-capture across the WorkOS redirect —
      // read the token NOW and hand it to the backend, which carries it to
      // the OAuth callback in a one-shot cookie. Dropping it here would
      // leave OAuth signups unattributed.
      const cometToken = currentCometToken();
      const fingerprintEventId = await getFingerprintEventId();
      const body =
        cometToken || fingerprintEventId
          ? { comet_token: cometToken, fingerprint_event_id: fingerprintEventId }
          : undefined;
      const res =
        provider === "google"
          ? await googleOauth(body)
          : await microsoftOauth(body);
      window.location.href = res.authorization_url;
    } catch {
      showToast.error("Sign in failed", {
        description: "Failed to start sign in. Please try again.",
      });
      setLoading(null);
    }
  }

  const btnClass =
    "w-full bg-contrast/4 border border-contrast/[0.15] text-heading font-medium text-sm rounded-md py-2.5 px-3.5 flex items-center justify-center gap-1.5 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-all opacity-40 cursor-not-allowed";

  return (
    <div className="flex flex-col gap-3 w-full">
      <button type="button" disabled className={btnClass} title="Coming soon">
        <GoogleIcon className="size-4 text-heading" />
        {label} with Google
        <span className="text-xs font-normal text-muted-foreground">
          (soon)
        </span>
      </button>
      <button type="button" disabled className={btnClass} title="Coming soon">
        <MicrosoftIcon className="size-4 text-heading" />
        {label} with Microsoft
        <span className="text-xs font-normal text-muted-foreground">
          (soon)
        </span>
      </button>
    </div>
  );
}
